import { MigrationHubBanner } from "@/components/dashboard/MigrationHubBanner";
import { RecentClients } from "@/components/dashboard/RecentClients";
import { UpcomingTasks } from "@/components/dashboard/UpcomingTasks";
import { RecentProspects } from "@/components/dashboard/RecentProspects";
import { RecentQuotes } from "@/components/dashboard/RecentQuotes";
import { UpcomingRenewals } from "@/components/dashboard/UpcomingRenewals";
import { MarketingCampaigns } from "@/components/dashboard/MarketingCampaigns";
import { UpcomingAppointments } from "@/components/dashboard/UpcomingAppointments";
import { RecentEligibilityChanges } from "@/components/dashboard/RecentEligibilityChanges";
import { UpcomingEnrollmentDeadlines } from "@/components/dashboard/UpcomingEnrollmentDeadlines";
import { UpcomingAgeEvents } from "@/components/dashboard/UpcomingAgeEvents";
import { MetricCard } from "@/components/dashboard/MetricCard";
import {
  Sparkles,
  X,
  Play,
  Building2,
  Target,
  Calendar,
  AlertCircle,
} from "lucide-react";
import { useBrokerTour } from "@/components/tours/BrokerTourManager";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/hooks/useAuth";
import { useQuery } from "@tanstack/react-query";
import { countClientsRenewingWithin } from "@shared/utils/renewals";

interface ClientLite {
  id: number;
  status?: string | null;
}
interface ProspectLite {
  id: number;
  status?: string | null;
}
interface ClientPlanLite {
  clientId: number;
  renewalDate?: string | null;
  endDate?: string | null;
  plan?: { renewalDate?: string | null; endDate?: string | null } | null;
}
interface TaskStatsLite {
  open: number;
}

const OPEN_PROSPECT_STATUSES = new Set([
  "lead",
  "qualified",
  "proposal_sent",
  "negotiating",
]);

export default function Dashboard() {
  const { user } = useAuth();
  const { shouldShowWelcome, startTour, dismissWelcome } = useBrokerTour();

  const getUserFirstName = () => {
    if (!user) return "";
    const name = (user as any).firstName || (user as any).name || "";
    return name.split(" ")[0];
  };

  // Top-level data for the primary metric cards. React Query caches by key,
  // so sub-components (RecentClients, RecentProspects, UpcomingTasks,
  // UpcomingRenewals) reading the same keys reuse the same response.
  const { data: clients = [] } = useQuery<ClientLite[]>({
    queryKey: ["/api/clients"],
  });
  const { data: prospects = [] } = useQuery<ProspectLite[]>({
    queryKey: ["/api/prospects"],
  });
  const { data: clientPlans = [] } = useQuery<ClientPlanLite[]>({
    queryKey: ["/api/clientplans"],
  });
  // Same query as the header badge and the /tasks "Open" card, so all three
  // show one number (DEV-331). Counting a page of /api/tasks client-side
  // capped at 20 rows and used a different scope.
  const { data: taskStats } = useQuery<TaskStatsLite>({
    queryKey: ["/api/tasks/stats"],
  });

  const activeClientsCount = clients.filter(
    (c) => (c.status ?? "active") === "active",
  ).length;
  // clientStatusEnum has a third value ("pending") — don't lump it into "inactive"
  const inactiveClientsCount = clients.filter((c) => c.status === "inactive").length;
  const pendingClientsCount = clients.filter((c) => c.status === "pending").length;
  const activeClientsSubtitle = [
    inactiveClientsCount > 0 ? `${inactiveClientsCount} inactive` : null,
    pendingClientsCount > 0 ? `${pendingClientsCount} pending` : null,
  ]
    .filter(Boolean)
    .join(", ");
  const openProspectsCount = prospects.filter((p) =>
    OPEN_PROSPECT_STATUSES.has(p.status ?? ""),
  ).length;
  // Same helper + active-client filter as /renewals, Reports and Calendar
  const renewalsSoonCount = countClientsRenewingWithin(clientPlans, 90, {
    clientIds: new Set(
      clients.filter((c) => (c.status ?? "active") === "active").map((c) => c.id),
    ),
  });
  const openTasksCount = taskStats?.open ?? 0;

  return (
    <div className="space-y-8">
      {/* Migration Hub progress reminder — shows above the welcome banner so
          brokers see the in-flight migration queue before anything else.
          Self-hides when there are no clients or all are migrated, or when
          the broker dismisses it for this session. */}
      <MigrationHubBanner />

      {/* Welcome Banner for New Brokers */}
      {shouldShowWelcome && (
        <Card className="bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20" data-tour="dashboard-welcome">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="rounded-full bg-primary/15 p-3">
                  <Sparkles className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Welcome! Let's set up your Agency.
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    Take a quick tour to learn about the key features and how to get started with managing your insurance operations.
                  </p>
                  <div className="flex gap-3">
                    <Button
                      onClick={startTour}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      Start Tour
                    </Button>
                    <Button
                      variant="outline"
                      onClick={dismissWelcome}
                    >
                      Maybe Later
                    </Button>
                  </div>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={dismissWelcome}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      {/* Dashboard Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-foreground">Dashboard</h2>
          <p className="text-muted-foreground mt-2">
            Welcome back{getUserFirstName() ? `, ${getUserFirstName()}` : ""}! Here's an overview of your insurance operations.
          </p>
        </div>
        {!shouldShowWelcome && (
          <Button
            onClick={startTour}
            variant="outline"
            className="flex items-center gap-2"
          >
            <Play className="h-4 w-4" />
            Take the Tour
          </Button>
        )}
      </div>

      {/* Primary Metric Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          color="blue"
          title="Active Clients"
          icon={Building2}
          value={activeClientsCount}
          subtitle={activeClientsSubtitle}
          href="/clients"
        />
        <MetricCard
          color="purple"
          title="Open Prospects"
          icon={Target}
          value={openProspectsCount}
          subtitle={
            prospects.length > 0
              ? `${prospects.length} total in pipeline`
              : "no prospects yet"
          }
          href="/prospects"
        />
        <MetricCard
          color="green"
          title="Renewals (90 days)"
          icon={Calendar}
          value={<span data-testid="dashboard-renewals-90">{renewalsSoonCount}</span>}
          subtitle="clients with plans renewing soon"
          href="/renewals"
        />
        <MetricCard
          color="orange"
          title="Open Tasks"
          icon={AlertCircle}
          value={openTasksCount}
          subtitle="awaiting action"
          href="/tasks"
        />
      </div>

      {/* Eligibility & Enrollment Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <RecentEligibilityChanges />
        <UpcomingEnrollmentDeadlines />
        <UpcomingAgeEvents />
      </div>

      {/* Recent Activity Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <RecentClients />
        <RecentProspects />
        <RecentQuotes />
      </div>

      {/* Secondary Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <UpcomingRenewals />
        <UpcomingTasks />
        <MarketingCampaigns />
      </div>

      {/* Calendar Integration */}
      <UpcomingAppointments />
    </div>
  );
}
